import type {
  FindFilterCountsInput,
  FindProductsInput,
} from '@travel-planner/shared/schemas';
import type { PlannerSourceSearch } from './source-search-params';

// Default rows per page; the URL `sourcePageSize` overrides it. The picker
// and the slide-down map both build through this so they share one cached
// request and the same page window.
export const PLANNER_DEFAULT_PAGE_SIZE = 20;

// 4-corner viewport bbox from the planner URL search — only when all corners
// are present (a partial bbox is treated as "no viewport filter").
export const bboxFromSourceSearch = (
  s: PlannerSourceSearch
): FindProductsInput['bbox'] | undefined =>
  typeof s.sourceNeLat === 'number' &&
  typeof s.sourceNeLng === 'number' &&
  typeof s.sourceSwLat === 'number' &&
  typeof s.sourceSwLng === 'number'
    ? {
        northEastLat: s.sourceNeLat,
        northEastLng: s.sourceNeLng,
        southWestLat: s.sourceSwLat,
        southWestLng: s.sourceSwLng,
      }
    : undefined;

// Maps the planner `source*` search into the products query input. The
// picker and the slide-down map both build through this so they share one
// cached result. `title` lets the picker pass its debounced value; the map
// mirrors the raw URL term.
export const toPlannerQueryInput = (
  s: PlannerSourceSearch,
  title: string | undefined = s.sourceSearch || undefined
): FindProductsInput => {
  const page = s.sourcePage ?? 1;
  const pageSize = s.sourcePageSize ?? PLANNER_DEFAULT_PAGE_SIZE;
  return {
    offset: (page - 1) * pageSize,
    limit: pageSize,
    title,
    contentCategory: s.sourceCategory,
    contentSubcategory: s.sourceSubcategory,
    catalogId: s.sourceCatalogId,
    country: s.sourceCountry,
    city: s.sourceCity || undefined,
    supplierName: s.sourceSupplierName || undefined,
    valueTier: s.sourceValueTier,
    customRegionIds: s.sourceRegionId ? [s.sourceRegionId] : undefined,
    bbox: bboxFromSourceSearch(s),
    // Catalog-hidden products must never be offered in the planner — they
    // can't be dragged into an itinerary the consultant is building.
    isHidden: false,
  };
};

// Maps the planner `source*` search into the `products.filterCounts` input.
// That endpoint accepts only catalogId | country | bbox | valueTier (NOT
// category/subcategory/search/region); each dimension's count excludes its
// own filter so the picker's dropdowns stay dynamic.
export const toPlannerFilterCountsInput = (
  s: PlannerSourceSearch
): FindFilterCountsInput => ({
  catalogId: s.sourceCatalogId,
  country: s.sourceCountry,
  valueTier: s.sourceValueTier,
  bbox: bboxFromSourceSearch(s),
});
